import * as _ from "lodash";
import { inject, observer } from "mobx-react";

import * as React from "react";
import {
    Alert,
    Button,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
    Tooltip,
    Col,
    OverlayTrigger,
    Container
} from "react-bootstrap";
import { Utils } from "../../common/utils";
import { CommitteeStore } from "../../stores";
import { Committee, Dashboard, Workbook } from "Stores/committeeStore";
import {ColumnDef, flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";

type SelectedItem = Dashboard | Workbook;

interface MappingSelectorProps {
    committeeStore?: CommitteeStore
    committee?: Committee
    handleClose: () => void
    updateCommittee: (id: number, c: () => void) => void
}

interface MappingSelectorState {
    selected: string[]
    shouldShowCurrent: boolean
    isUpdating: boolean
    didUpdate: boolean
    showFilters: boolean
    saved: boolean
    dashboardFilter: string
}
/*
* Currently dashboards are differentiated by their tableau workbook_id
* If a workbook is used by more than one dashboard, we'll need to change this
* to use dashboard id instead*
* */

interface SelectButtonProps {
    onSelect: (selectedItem: SelectedItem) => void
    selectedItem: SelectedItem
    committee: Committee
    getIsSelected: (selectedItem: SelectedItem) => boolean
}

class SelectButton extends React.Component<SelectButtonProps>{
    handleClick = () => {
        this.props.onSelect(this.props.selectedItem);
    };

    render(){
        return (this.props.committee.protected && !Utils.isSuperAdmin()
            ? <OverlayTrigger
                key={"top"}
                placement={"top"}
                overlay={
                    <Tooltip id={"tooltip-top"}>
                      Protected
                    </Tooltip>
                }>
                <i className="icon icon-lock topaz-icon"/>
            </OverlayTrigger>
            : <Button id={`dashboard-permissions-select-button-${this.props.committee.id}`} onClick={ this.handleClick } className="add-remove-button btn-icon">
                { this.props.getIsSelected(this.props.selectedItem) ? <i className="icon icon-check-square"/> : <i className="icon icon-square-o"/> }
            </Button>);
    }
}


@inject("commonStore", "committeeStore")
@observer
export class DashboardMappingSelector extends React.Component<MappingSelectorProps, MappingSelectorState> {

    getSelected = () => {
        const dbs = this.props.committee && this.props.committee.dashboards ? this.props.committee.dashboards : [];
        return dbs ? dbs.map(d => d.id) : [];
    };

    isUnmounted = false;

    state = {
        selected: this.getSelected(),
        shouldShowCurrent: true,
        isUpdating: false,
        didUpdate: false,
        showFilters: false,
        saved: false,
        dashboardFilter: ""
    };

    showCurrent = () => {
        this.setState({shouldShowCurrent: true});
    };

    hideCurrent = () => {
        this.setState({shouldShowCurrent: false});
    };

    save = () => {
        this.setState({isUpdating: true, saved: false}, () => {
            if(!this.props.committee || !this.props.committeeStore){
                return;
            }
            const current: string[] = this.props.committee.dashboards? this.props.committee.dashboards.slice().map(obj => obj.id) : [];

            // Diff prev and next
            const add = _.difference(this.state.selected, current);
            const remove = _.difference(current, this.state.selected);

            this.props.committeeStore.setDashboardUpdateCount(add.length, remove.length);

            Promise.all([
                this.props.committeeStore.addWorkbooksToCommittee(this.props.committee.id, add),
                this.props.committeeStore.removeWorkbooksFromCommittee(this.props.committee.id, remove)
            ]).then(() => {
                this.props.committeeStore!.fetchCommittees();
                this.props.updateCommittee(this.props.committee!.id, () => {
                    if(!this.isUnmounted) {
                        this.setState({
                            saved: add.length > 0 || remove.length > 0
                        }, () => {
                            if(!this.isUnmounted) {
                                this.setState({isUpdating: false});
                            }
                        });
                    }
                });
            });
        });
    };

    resetCounts = () => {
        if(!this.props.committeeStore){
            console.error("No committees loaded, can't reset map count");
            return;
        }

        this.setState({saved: false});
        this.props.committeeStore.resetAddedMapCount();
        this.props.committeeStore.resetRemovedMapCount();
    };

    exit = () => {
        if(!this.props.committeeStore){
            console.error("No committees loaded, can't save");
            return;
        }
        this.resetCounts();
        this.props.handleClose();
    };

    isRowSelected = (row: SelectedItem): boolean => {
        const selectedIdx = this.state.selected.indexOf(row.id);
        return selectedIdx >= 0;
    };

    onSelect = (row: SelectedItem): void => {
        const newSelected = [...this.state.selected];

        if( row && !this.isRowSelected(row) ) {
            newSelected.push(row.id);
            this.setState({selected: newSelected});
        } else if( row ) {
            const idx = this.state.selected.indexOf(row.id);
            newSelected.splice(idx, 1);
            this.setState({selected: newSelected});
        }
    };

    columns: ColumnDef<SelectedItem>[] =
        [{
            id: "selector",
            cell: (props) => (<SelectButton committee={this.props.committee!} selectedItem={props.row.original as SelectedItem} getIsSelected={this.isRowSelected} onSelect={this.onSelect} />),
            enableColumnFilter: false,
            enableSorting: false
        }, {
            header: "Name",
            accessorKey: "name",
        }];

    handleFilter = (ev: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({dashboardFilter: ev.target.value});
    };

    componentWillUnmount(){
        this.isUnmounted = true;
    }

    componentDidMount(){
        if( !this.props.committeeStore || !this.props.committee){
            console.error("Error loading committees, please try again");
            return;
        }
    }

    handleDismiss = () => {
        this.props.handleClose();
    };

    toggleFilters = () => {
        this.setState({ showFilters: !this.state.showFilters });
    };

    render() {
        if(!this.props.committeeStore || !this.props.committee){
            return null;
        }

        const current: Dashboard[] = this.props.committee.dashboards ? this.props.committee.dashboards.slice() : [];
        let workbooks: Workbook[] = this.props.committeeStore.allWorkbooks ? this.props.committeeStore.allWorkbooks.slice() : [];
        workbooks = this.state.dashboardFilter !== ""
            ? workbooks.filter((workbook: Workbook) =>
                workbook.name.toLowerCase().includes(this.state.dashboardFilter.toLowerCase()))
            : workbooks;

        return (
            <Modal centered scrollable show={true} onHide={this.handleDismiss} dialogClassName="modal-container modal-committees-dashboards wide mapping">
                <Modal.Header>
                    <Container>
                        <Row>
                            <Col xs={12} xl={3} className="text-xl-start">
                                <span className="modal-title">Dashboards</span>
                            </Col>
                            <Col xs={12} xl={9} className="actions text-xl-end">
                                <Row>
                                    <Col>
                                        <Button variant="outline-light" id="current-dashboards-button" className={this.state.shouldShowCurrent ? "active" : ""}
                                                onClick={this.showCurrent}>CURRENT DASHBOARDS</Button>
                                    </Col>
                                    <Col>
                                        <Button variant="outline-light" id="all-dashboards-button" className={!this.state.shouldShowCurrent ? "active" : ""}
                                                disabled={this.props.committee.protected && !Utils.isSuperAdmin()}
                                                onClick={this.hideCurrent} >ALL DASHBOARDS</Button>
                                    </Col>
                                    <Col>
                                        <Form.Control id="filter-dashboards-field" className="filter icon" disabled={this.state.shouldShowCurrent}
                                                      onChange={this.handleFilter}/>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Container>
                </Modal.Header>
                <Modal.Body className={"mapping-selector list-container" + (this.state.showFilters ? " show-filters" : "")}>
                    <div className="action-container">
                        <div className="list-actions">
                            <div className="left">
                                <Button onClick={this.toggleFilters} style={{marginTop: "-5px"}}>
                                    {`${this.state.showFilters ? "Close" : "Open" } Filters `}<i className={"icon " + (this.state.showFilters ? "icon-angle-up" : "icon-angle-down")}/>
                                </Button>
                            </div>
                        </div>
                    </div>
                    {!this.props.committee || !this.props.committeeStore
                    ? <div style={{textAlign: "center", paddingTop: 50}}><Spinner animation={"border"} variant={"secondary"} /></div>
                    : this.state.shouldShowCurrent || (this.props.committee.protected && !Utils.isSuperAdmin())
                        ? <DashboardsListReactTable columns={this.columns} defaultData={current}/>
                            : <DashboardsListReactTable columns={this.columns} defaultData={workbooks || []}/>}
                </Modal.Body>
                <Modal.Footer>
                    { this.state.isUpdating ?
                        <Alert className="inline-message">
                            {"Updating mappings."}
                        </Alert> : null }
                    { this.props.committeeStore && this.state.saved ?
                        <Alert
                            className="inline-message"
                            onClose={ this.resetCounts } dismissible>
                            {`Added ${this.props.committeeStore.addMapCount || 0} mappings. `}
                            {`Removed ${this.props.committeeStore.removeMapCount || 0} mappings.`}
                        </Alert> : null}
                        <Button
                            id="dashboard-permissions-exit-button"
                            onClick={this.exit}
                            variant="secondary"
                            size="lg">
                            Exit
                        </Button>
                        <Button
                            id="dashboard-permissions-save-button"
                            className="ml-2"
                            onClick={this.save}
                            variant="primary"
                            size="lg"
                            disabled={this.state.isUpdating || this.props.committeeStore.isProcessing}>
                            {this.state.isUpdating || this.props.committeeStore.isProcessing ? <Spinner animation="border" variant={"secondary"}/> : "Save"}</Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

interface DashboardsListReactTableProps {
    columns: ColumnDef<SelectedItem>[]
    defaultData: SelectedItem[],
}

function DashboardsListReactTable({columns, defaultData}: DashboardsListReactTableProps) {
    const table = useReactTable({data: defaultData, columns, getCoreRowModel: getCoreRowModel()});
    return (
        <Table hover>
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                        return (
                            <th key={header.id}>
                                {header.isPlaceholder ? null : (
                                    <div>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                    </div>
                                )}
                            </th>
                        );
                    })}
                </tr>
            ))}
            </thead>
            <tbody>
            {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                        <td key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    ))}
                </tr>
            ))}
            {table.getRowModel().rows.length === 0 && <tr><td colSpan={2} className="text-center">No dashboards</td></tr>}
            </tbody>
        </Table>
    );
}
